import React, { useState } from "react";
import { Button } from "../ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "../ui/card";
import { Badge } from "../ui/badge";
import { useData } from "../context/data/DataState";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit,
  Eye,
  Users,
  Share,
  MoreVertical,
  Copy,
  Calendar,
  Trophy,
  TrendingUp,
  Search,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Input } from "../ui/input";
import DashboardStats from "./DashboardStats";

const AdminHome = () => {
  const { data, changeCommunityId, seteditFlag, userPersonalData } = useData();
  const [searchQuery, setSearchQuery] = useState("");

  const creatorAndAdminCommunities = Object.values(data.community || {}).filter(
    (community) => community.role === "Creator" || community.role === "Admin"
  );

  // Filter communities based on search
  const filteredCommunities = creatorAndAdminCommunities.filter((community) =>
    community.community_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const navigate = useNavigate();

  const handleCreateCommunity = () => {
    seteditFlag(false);
    navigate("/admin/form");
  };

  const handleEditCommunity = (id) => {
    changeCommunityId(id);
    seteditFlag(true);
    navigate("/admin/form/" + id);
  };

  const handleViewCommunity = (id) => {
    changeCommunityId(id);
    seteditFlag(false);
    navigate("/");
  };

  const handleCopyInvite = (communityName, communityCode) => {
    let link = `${import.meta.env.VITE_BASE_URL}/join/${communityName}/${communityCode}`;
    link = encodeURI(link);
    let text = `Follow this link to join my community, ${communityName}, on Healthier Me: ${link}`;
    navigator.clipboard.writeText(text);
  };

  const handleShare = async (communityName, communityCode = "") => {
    let link = `${import.meta.env.VITE_BASE_URL}/join/${communityName}/${communityCode}`;
    link = encodeURI(link);
    let text = `Follow this link to join my community, ${communityName}, on Healthier Me: ${link}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Healthier Me",
          text: text,
        });
      } else {
        handleCopyInvite(communityName, communityCode);
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  // Calculate community stats
  const getCommunityStats = (community) => {
    const seasons = Object.values(community.season || {});
    const activeSeasons = seasons.filter((season) => {
      const now = new Date();
      const start = new Date(season.start_date);
      const end = new Date(season.end_date);
      return now >= start && now <= end;
    });

    const totalChallenges = seasons.reduce((sum, season) => {
      return sum + Object.keys(season.challenge || {}).length;
    }, 0);

    return {
      memberCount: Number(community.member_count) || 0,
      seasonCount: seasons.length,
      activeSeasonCount: activeSeasons.length,
      challengeCount: totalChallenges,
      isActive: activeSeasons.length > 0,
    };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Admin Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your communities, seasons, and challenges
            </p>
          </div>
          {Boolean(userPersonalData.allow_community_creation) && (
            <Button
              onClick={handleCreateCommunity}
              className="gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              size="lg"
            >
              <Plus className="h-5 w-5" />
              Create Community
            </Button>
          )}
        </div>

        {/* Stats Cards */}
        <DashboardStats communities={creatorAndAdminCommunities} />

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search communities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Communities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCommunities.length === 0 && searchQuery === "" && (
            <div className="col-span-full text-center py-16">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                No Communities Yet
              </h2>
              <p className="text-gray-600 mb-6">
                You are not a Creator or Admin of any communities
              </p>
              {Boolean(userPersonalData.allow_community_creation) && (
                <Button onClick={handleCreateCommunity} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create Your First Community
                </Button>
              )}
            </div>
          )}

          {filteredCommunities.length === 0 && searchQuery !== "" && (
            <div className="col-span-full text-center py-16">
              <p className="text-gray-600">
                No communities found matching "{searchQuery}"
              </p>
            </div>
          )}

          {filteredCommunities.map((community) => {
            const stats = getCommunityStats(community);
            return (
              <Card
                key={community.community_id}
                className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-blue-200"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white text-lg font-bold">
                          {community.community_name[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg truncate">
                          {community.community_name}
                        </CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant={
                              community.role === "Creator"
                                ? "default"
                                : "secondary"
                            }
                            className="text-xs"
                          >
                            {community.role}
                          </Badge>
                          {stats.isActive && (
                            <Badge
                              variant="outline"
                              className="text-xs border-green-500 text-green-700"
                            >
                              Active
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            handleCopyInvite(
                              community.community_name,
                              community.community_join_code
                            )
                          }
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Invite Link
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() =>
                            handleShare(
                              community.community_name,
                              community.community_join_code
                            )
                          }
                        >
                          <Share className="h-4 w-4 mr-2" />
                          Share Community
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() =>
                            navigate("/admin/community/" + community.community_id)
                          }
                        >
                          <Users className="h-4 w-4 mr-2" />
                          Manage Members
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>

                <CardContent className="pb-3">
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {community.description || "No description provided"}
                  </p>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div className="text-center p-2 bg-blue-50 rounded-lg">
                      <Users className="h-4 w-4 mx-auto text-blue-600 mb-1" />
                      <p className="text-lg font-bold text-gray-900">
                        {stats.memberCount}
                      </p>
                      <p className="text-xs text-gray-600">Members</p>
                    </div>
                    <div className="text-center p-2 bg-purple-50 rounded-lg">
                      <Calendar className="h-4 w-4 mx-auto text-purple-600 mb-1" />
                      <p className="text-lg font-bold text-gray-900">
                        {stats.seasonCount}
                      </p>
                      <p className="text-xs text-gray-600">Seasons</p>
                    </div>
                    <div className="text-center p-2 bg-amber-50 rounded-lg">
                      <Trophy className="h-4 w-4 mx-auto text-amber-600 mb-1" />
                      <p className="text-lg font-bold text-gray-900">
                        {stats.challengeCount}
                      </p>
                      <p className="text-xs text-gray-600">Challenges</p>
                    </div>
                  </div>

                  {/* Join Code */}
                  <div
                    className="text-sm text-gray-500 bg-gray-50 p-2 rounded cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        community.community_join_code
                      );
                    }}
                    title="Click to copy"
                  >
                    <span className="font-mono">
                      Code: {community.community_join_code}
                    </span>
                  </div>
                </CardContent>

                <CardFooter className="flex gap-2 pt-3 border-t">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditCommunity(community.community_id)}
                    className="flex-1"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewCommunity(community.community_id)}
                    className="flex-1"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AdminHome;
